/**
 * /insta — тиждень inFLOW за підписку на Instagram (owner 2026-10-14).
 *
 * Людина підписується на @its_yehor, вставляє посилання на свою сторінку й
 * надсилає заявку. Власник перевіряє вручну в каналі «Підписники Instagram» і
 * натискає «Зарахувати» чи «Відмовити» — відповідь приходить у бот. Зараховано
 * → промокод `insta7`: 7 днів за 1 ₴ (або +7 днів до живої підписки).
 *
 * Каркас стандартний: BackNav → PageHeader (з subtitle) → Section.
 */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ActionButton } from "@/components/ActionButton";
import { BackNav } from "@/components/BackNav";
import { Icon } from "@/components/Icon";
import { PageHeader } from "@/components/PageHeader";
import { Section } from "@/components/Section";
import { Spinner } from "@/components/Spinner";
import { useInstaPromoState, useSubmitInstaPromo } from "@/api/hooks";
import { showAppAlert } from "@/lib/appAlert";
import { tg } from "@/lib/telegram";

function openInstagram(url: string) {
  const webapp = tg();
  if (webapp?.openLink) webapp.openLink(url);
  else window.open(url, "_blank", "noopener");
}

export function InstaPromoPage() {
  const navigate = useNavigate();
  const stateQuery = useInstaPromoState();
  const submitMut = useSubmitInstaPromo();
  const [link, setLink] = useState("");

  if (stateQuery.isLoading) return <Spinner />;
  const st = stateQuery.data;
  const handle = st?.owner_handle ?? "its_yehor";
  const ownerUrl = st?.owner_url ?? `https://instagram.com/${handle}`;
  const status = st?.status ?? "none";

  const header = (subtitle: string) => (
    <>
      <BackNav />
      <PageHeader
        eyebrow="подарунок"
        title="Отримай тиждень підписки на inFLOW"
        titleLead="Отримай тиждень"
        titleAccent="підписки на inFLOW"
        scribbleWidth={250}
        subtitle={subtitle}
      />
    </>
  );

  if (status === "pending") {
    return (
      <>
        {header(
          "Заявка вже в нас. Глянемо підписку й напишемо тобі в бот.",
        )}
        {st?.submitted_url && (
          <Section title="Твоя сторінка">
            <div className="px-2.5 py-3 text-[0.875rem] text-ink [overflow-wrap:anywhere]">
              {st.submitted_url}
            </div>
          </Section>
        )}
        <ActionButton tone="secondary" onClick={() => navigate("/")}>
          На головну
        </ActionButton>
      </>
    );
  }

  if (status === "approved") {
    const subtitle = st?.granted
      ? "Ти серед підписників засновника — 7 днів уже додали до твоєї підписки."
      : st?.claimed
        ? "Тиждень за підписку вже твій."
        : "Ти серед підписників засновника. Тиждень повного доступу чекає — за 1 ₴.";
    return (
      <>
        {header(subtitle)}
        {st?.can_claim ? (
          <ActionButton onClick={() => navigate(st.promo_path)}>
            Забрати тиждень за 1 ₴
          </ActionButton>
        ) : (
          <ActionButton tone="secondary" onClick={() => navigate("/")}>
            На головну
          </ActionButton>
        )}
      </>
    );
  }

  const send = async () => {
    const value = link.trim();
    if (!value || submitMut.isPending) return;
    try {
      await submitMut.mutateAsync(value);
      tg()?.HapticFeedback?.notificationOccurred?.("success");
      setLink("");
    } catch (e) {
      tg()?.HapticFeedback?.notificationOccurred?.("error");
      showAppAlert((e as Error).message);
    }
  };

  return (
    <>
      {header(
        status === "rejected"
          ? "Минулого разу не побачили тебе серед підписників засновника. Підпишись і надішли сторінку ще раз — глянемо знову."
          : "Підпишись на Instagram засновника inFLOW і встав посилання на свою сторінку — ми перевіримо підписку й надішлемо тобі промокод на 7 днів повного доступу.",
      )}

      <Section title="Крок 1 · підпишись">
        <button
          type="button"
          onClick={() => openInstagram(ownerUrl)}
          className="flex w-full items-center gap-3 p-4 text-left"
        >
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[12px] bg-primary/10 text-primary">
            <Icon name="instagram" size={20} />
          </span>
          <span className="min-w-0 flex-1">
            <span className="block font-display text-[0.9375rem] font-bold text-ink">
              @{handle}
            </span>
            {/* Один рядок (owner 2026-10-14: «дуже товстий блок»). */}
            <span className="block truncate text-[0.8125rem] text-ink-dim">
              Відкрий і натисни «Стежити»
            </span>
          </span>
          <Icon name="arrow-right" size={14} color="#A8A29C" />
        </button>
      </Section>

      <Section title="Крок 2 · надішли свою сторінку">
        <div className="p-2.5">
          <input
            type="url"
            inputMode="url"
            autoCapitalize="off"
            autoCorrect="off"
            spellCheck={false}
            value={link}
            onChange={(e) => setLink(e.target.value)}
            maxLength={300}
            placeholder="instagram.com/твійнік"
            className="w-full rounded-lg bg-bg px-3 py-2.5 text-[0.875rem] text-ink caret-primary placeholder:text-ink-faint focus:outline-none focus:ring-2 focus:ring-primary/30"
          />
          {/* Два приклади, кожен в один рядок (owner 2026-10-14). */}
          <div className="mt-2 space-y-0.5 px-0.5 text-[0.8125rem] text-ink-dim">
            <div className="truncate">Посилання: instagram.com/твійнік</div>
            <div className="truncate">Або просто нік: @твійнік</div>
          </div>
        </div>
      </Section>

      <ActionButton onClick={send} disabled={!link.trim() || submitMut.isPending}>
        {submitMut.isPending ? "Надсилаємо…" : "Надіслати на перевірку"}
      </ActionButton>
    </>
  );
}
